import type { FC } from "react";

import { usePendingInviteCount } from "../../hooks/usePendingInviteCount";
import { useTabParam } from "../../hooks/useTabParam";
import AgentModeBudgetsPanel from "../agentConfig/AgentModeBudgetsPanel";
import { Tabs } from "../common/Tabs";
import { SchemaTelemetryPanel } from "../schemaTelemetry";
import { ApiKeysPanel } from "./ApiKeysPanel";
import { BillingPanel } from "./BillingPanel";
import { JiraSetupPanel } from "./JiraSetupPanel";
import { McpServersPanel } from "./McpServersPanel";
import { McpTokensPanel } from "./McpTokensPanel";
import { MyInvitesPanel } from "./MyInvitesPanel";
import { OrganizationPanel } from "./OrganizationPanel";
import { SkillMeshPanel } from "./SkillMeshPanel";

type SettingsTab =
  | "budgets"
  | "telemetry"
  | "organization"
  | "my-invites"
  | "billing"
  | "mcp-servers"
  | "mcp-tokens"
  | "skill-mesh"
  | "api-keys"
  | "jira";

const TABS = [
  { id: "budgets" as const, label: "Agent Budgets" },
  { id: "telemetry" as const, label: "Schema Telemetry" },
  { id: "organization" as const, label: "Organization" },
  { id: "my-invites" as const, label: "My Invites" },
  { id: "billing" as const, label: "Billing" },
  { id: "mcp-tokens" as const, label: "MCP Tokens" },
  { id: "skill-mesh" as const, label: "Skill Mesh MCP" },
  { id: "mcp-servers" as const, label: "MCP Servers" },
  { id: "api-keys" as const, label: "API Keys" },
  { id: "jira" as const, label: "Jira" },
];

function isSettingsTab(value: string): value is SettingsTab {
  return TABS.some((tab) => tab.id === value);
}

export const SettingsPanel: FC = () => {
  const [activeTab, setTab] = useTabParam<SettingsTab>("budgets");
  const { count: pendingInviteCount } = usePendingInviteCount();

  const tabs = TABS.map((tab) =>
    tab.id === "my-invites" ? { ...tab, badgeCount: pendingInviteCount } : tab,
  );

  return (
    <div className="space-y-4">
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={(id) => {
          if (isSettingsTab(id)) {
            setTab(id);
          }
        }}
      />

      {activeTab === "budgets" && <AgentModeBudgetsPanel />}
      {activeTab === "telemetry" && <SchemaTelemetryPanel />}
      {activeTab === "organization" && <OrganizationPanel />}
      {activeTab === "my-invites" && <MyInvitesPanel />}
      {activeTab === "billing" && <BillingPanel />}
      {activeTab === "mcp-tokens" && <McpTokensPanel />}
      {activeTab === "skill-mesh" && <SkillMeshPanel />}
      {activeTab === "mcp-servers" && <McpServersPanel />}
      {activeTab === "api-keys" && <ApiKeysPanel />}
      {activeTab === "jira" && <JiraSetupPanel />}
    </div>
  );
};
